// components/EditTraining.tsx
import { useState, useContext, useEffect } from 'react';
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ModalContainer, UIcon, IconContainer, MainContentContainer1,
  SubmitContainer, StyleButton, StyleInput, StyleLabel
} from '../Modal/CreateAdminUser';
import { FilterDropDown } from '../../../../ui-components';
import { useSettingModal } from '../../../../hooks/Context';
import apiCall from '../../../../utils/apiCall';
import { NotificationContext } from '../../../../providers/notification';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';


interface EditTrainingProps {
  training: {
    TrainingId: number;
    TrainingName: string;
    TrainingVenue: string;
    TrainingDate: string;
    TrainingEndDate: string; // Add TrainingEndDate here
    RouteId: number; 
    IsActive?: boolean;
  };
}

const EditTraining = ({ training }: EditTrainingProps) => {
  const [TrainingName, setTrainingName] = useState('');
  const [TrainingVenue, setTrainingVenue] = useState('');
  const [RouteId, setRouteId] = useState<number | null>(null);
  const [date, setDate] = useState('');
  const [endDate, setEndDate] = useState(''); // State for TrainingEndDate
  const [time, setTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [routeOptions, setRouteOptions] = useState<{ value: number; label: string }[]>([]);

  const { showNotification } = useContext(NotificationContext);
  const { setShowModal } = useSettingModal();
  const queryClient = useQueryClient();

useEffect(() => {
  if (training) {
    setTrainingName(training.TrainingName || '');
    setTrainingVenue(training.TrainingVenue || '');

    if (training.TrainingDate) {
      // split date and time safely
      const [d, t] = training.TrainingDate.split('T');
      setDate(d);

      if (t) {
        const [hh, mm] = t.split(':');
        setTime(`${hh}:${mm}`); // keep exactly what backend sent
      } else {
        setTime('');
      }
    } else {
      setDate('');
      setTime('');
    }

    setIsActive(training?.IsActive ?? false);
    setRouteId(training.RouteId);

    if (training.TrainingEndDate) {
      const [endDateOnly] = training.TrainingEndDate.split('T');
      setEndDate(endDateOnly);
    } else {
      setEndDate('');
    }
  }
}, [training]);



  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        const response = await apiCall().get('/Routes/GetAllRoute');
        const data = response?.data?.Data || [];
        const formatted = data.map((route: any) => ({
          value: route.RouteId,
          label: route.RouteName
        }));
        setRouteOptions(formatted);
      } catch (error) {
        console.error('Error fetching routes:', error);
      }
    };

    fetchRoutes();
  }, []);

 const handleUpdateTraining = async () => {
  const TrainingDate = date && time ? `${date}T${time}:00` : null;
  const TrainingEndDate = endDate && time ? `${endDate}T${time}:00` : null;

  const data = {
    TrainingId: training.TrainingId,
    TrainingName,
    TrainingVenue,
    TrainingDate,
    TrainingEndDate,
    IsActive: isActive,
    RouteId,
  };

  setLoading(true);
  return (await apiCall().post('/SetUp/CreateUpdateTraining', data)).data;
};


  const mutation = useMutation(handleUpdateTraining, {
  onSuccess: () => {
    showNotification({ type: 'success', message: 'Training updated successfully' });
    setShowModal(false);
    queryClient.invalidateQueries(['all-training']);
  },
  onError: (error: any) => {
    showNotification({ type: 'error', message: error.message });
  },
  onSettled: () => setLoading(false),
});

const handleForm = (e: any) => {
  e.preventDefault();

  // ✅ Only validate if both start + end exist
  if (date && time && endDate) {
    const trainingStartDate = new Date(`${date}T${time}`);
    const trainingEndDate = new Date(`${endDate}T${time}`);

    if (trainingEndDate <= trainingStartDate) {
      return showNotification({
        type: 'error',
        message: 'Training End Date must be after the Training Date',
      });
    }
  }

  // ✅ Trigger update
  mutation.mutate();
};


  return (
    <ModalContainer>
      <div>
        <div>
          <div>
            <IconContainer><UIcon /></IconContainer>
            <span>Edit Training</span>
          </div>
        </div>
        <MainContentContainer1 onSubmit={handleForm}>
          <StyleLabel>
            <strong>Name</strong>
            <StyleInput value={TrainingName} onChange={(e) => setTrainingName(e.target.value)} />
          </StyleLabel>
          <StyleLabel>
            <strong>Venue</strong>
            <StyleInput value={TrainingVenue} onChange={(e) => setTrainingVenue(e.target.value)} />
          </StyleLabel>
          <StyleLabel>
            <strong>Route</strong>
            <FormControl fullWidth size="small">
              <Select
                labelId="route-select-label"
                value={RouteId ?? ''}
                onChange={(e) => setRouteId(e.target.value === '' ? null : Number(e.target.value))}
                displayEmpty
                sx={{
                  backgroundColor: '#11643C',
                  color: '#fff',
                  '& .MuiSvgIcon-root': { color: '#fff' },
                }}
              >
                <MenuItem value="">
                  <em>Select route</em>
                </MenuItem>
                {routeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </StyleLabel>
          <StyleLabel>
            <strong>Start Date</strong>
            <StyleInput value={date} onChange={(e) => setDate(e.target.value)} type='date' />
          </StyleLabel>
          <StyleLabel>
            <strong>End Date</strong>
            <StyleInput value={endDate} onChange={(e) => setEndDate(e.target.value)} type='date' /> {/* New End Date Input */}
          </StyleLabel>
          <StyleLabel>
            <strong>Time</strong>
            <StyleInput
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)} // stays as "20:00"
            />
          </StyleLabel>
          <StyleLabel>
            <strong>Status</strong>
            <select
              value={isActive ? 'true' : 'false'}
              onChange={(e) => setIsActive(e.target.value === 'true')}
              style={{
                padding: '10px',
                border: '1px solid lightgray',
                borderRadius: '4px',
                width: '100%',
              }}
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </StyleLabel>

          <SubmitContainer>
            <span onClick={() => setShowModal(false)}>Cancel</span>
            <StyleButton type="submit">{!loading ? 'Update' : 'Updating...'}</StyleButton>
          </SubmitContainer>
        </MainContentContainer1>
      </div>
    </ModalContainer>
  );
};

export default EditTraining;
